import { useEffect, useRef, useState } from 'react';
import { Archive, ArrowLeft, Forward, Inbox, MailOpen, Reply, ReplyAll, Star, Trash2 } from 'lucide-react';
import { ApiError } from '../../lib/apiClient';
import { getThread, moveThread, setThreadState } from '../../lib/mailApi';
import { formatAddress, formatFullDate, senderLabel } from '../../lib/mailFormat';
import type {
  MailMessageDto,
  MailMovableFolder,
  MailSendMode,
  MailThreadDetailDto,
  MailThreadSummaryDto,
} from '../../lib/mailTypes';
import { useApiResource } from '../../lib/useApiResource';
import { MailMessageBody } from './MailMessageBody';

/** Asks the page to open the compose window for `message` of `thread`. */
export type ComposeRequest = (
  mode: Exclude<MailSendMode, 'new'>,
  thread: MailThreadSummaryDto,
  message: MailMessageDto,
) => void;

export interface ThreadChange {
  summary: MailThreadSummaryDto;
  /** The thread no longer belongs in the open view (moved folder / marked unread). */
  leave: boolean;
}

function AuthLine({ auth }: { auth: NonNullable<MailMessageDto['auth']> }) {
  const entries = (['spf', 'dkim', 'dmarc'] as const).filter((k) => auth[k]);
  if (entries.length === 0) return null;
  const bad = entries.some((k) => auth[k]?.toLowerCase() !== 'pass');
  return (
    <div className={bad ? 'mail-auth mail-auth-warn' : 'mail-auth'}>
      {entries.map((k) => `${k.toUpperCase()} ${auth[k]}`).join(' · ')}
    </div>
  );
}

function ReplyActions({ onAction, disabled }: { onAction: (mode: Exclude<MailSendMode, 'new'>) => void; disabled?: boolean }) {
  return (
    <>
      <button type="button" className="btn btn-xs" disabled={disabled} onClick={() => onAction('reply')}>
        <Reply size={14} aria-hidden /> Reply
      </button>
      <button type="button" className="btn btn-xs" disabled={disabled} onClick={() => onAction('replyAll')}>
        <ReplyAll size={14} aria-hidden /> Reply all
      </button>
      <button type="button" className="btn btn-xs" disabled={disabled} onClick={() => onAction('forward')}>
        <Forward size={14} aria-hidden /> Forward
      </button>
    </>
  );
}

function MessageCard({
  threadId,
  message,
  expanded,
  onToggle,
  onCompose,
}: {
  threadId: string;
  message: MailMessageDto;
  expanded: boolean;
  onToggle: () => void;
  onCompose?: (mode: Exclude<MailSendMode, 'new'>) => void;
}) {
  const when = formatFullDate(message.receivedAt ?? message.sentAt);
  return (
    <article className={expanded ? 'mail-message open' : 'mail-message'}>
      <button type="button" className="mail-message-head" aria-expanded={expanded} onClick={onToggle}>
        <span className="mail-message-from">
          {message.direction === 'outbound' ? 'You' : senderLabel(message.from)}
        </span>
        {!expanded && <span className="mail-message-preview">{message.text.slice(0, 120)}</span>}
        <span className="mail-thread-time">{when}</span>
      </button>
      {expanded && (
        <div className="mail-message-detail">
          <div className="cell-sub">From: {formatAddress(message.from)}</div>
          {message.to.length > 0 && <div className="cell-sub">To: {message.to.map(formatAddress).join(', ')}</div>}
          {message.cc.length > 0 && <div className="cell-sub">Cc: {message.cc.map(formatAddress).join(', ')}</div>}
          {message.auth && <AuthLine auth={message.auth} />}
          <MailMessageBody threadId={threadId} message={message} />
          {onCompose && (
            <div className="mail-message-actions" role="group" aria-label="Respond to this message">
              <ReplyActions onAction={onCompose} />
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function ThreadBody({
  detail,
  onBack,
  onChanged,
  onCompose,
}: {
  detail: MailThreadDetailDto;
  onBack: () => void;
  onChanged: (change: ThreadChange) => void;
  onCompose?: ComposeRequest;
}) {
  const [thread, setThread] = useState(detail.thread);
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(detail.messages.length ? [detail.messages[detail.messages.length - 1].id] : []),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<MailThreadSummaryDto>, leave: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const summary = await action();
      setThread(summary);
      onChanged({ summary, leave });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const move = (folder: MailMovableFolder) => run(() => moveThread(thread.id, folder), true);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <div className="mail-thread-view">
      <div className="mail-thread-toolbar">
        <button type="button" className="btn btn-xs mail-back" onClick={onBack}>
          <ArrowLeft size={14} aria-hidden /> Back
        </button>
        {onCompose && detail.messages.length > 0 && (
          <span className="mail-thread-reply" role="group" aria-label="Respond to the conversation">
            <ReplyActions
              onAction={(mode) => onCompose(mode, thread, detail.messages[detail.messages.length - 1])}
            />
          </span>
        )}
        {thread.folder !== 'archive' && (
          <button type="button" className="btn btn-xs" disabled={busy} onClick={() => void move('archive')}>
            <Archive size={14} aria-hidden /> Archive
          </button>
        )}
        {thread.folder !== 'trash' && (
          <button type="button" className="btn btn-xs" disabled={busy} onClick={() => void move('trash')}>
            <Trash2 size={14} aria-hidden /> Trash
          </button>
        )}
        {thread.folder !== 'inbox' && thread.folder !== 'sent' && (
          <button type="button" className="btn btn-xs" disabled={busy} onClick={() => void move('inbox')}>
            <Inbox size={14} aria-hidden /> Move to inbox
          </button>
        )}
        <button
          type="button"
          className="btn btn-xs"
          disabled={busy}
          onClick={() => void run(() => setThreadState(thread.id, { read: false }), true)}
        >
          <MailOpen size={14} aria-hidden /> Mark unread
        </button>
        <button
          type="button"
          className="btn btn-xs"
          disabled={busy}
          aria-pressed={thread.starred}
          onClick={() => void run(() => setThreadState(thread.id, { starred: !thread.starred }), false)}
        >
          <Star size={14} fill={thread.starred ? 'currentColor' : 'none'} aria-hidden />{' '}
          {thread.starred ? 'Unstar' : 'Star'}
        </button>
      </div>
      {error && (
        <div role="alert" className="error-banner">
          {error}
        </div>
      )}
      <h2 className="mail-thread-title">{thread.subject || '(no subject)'}</h2>
      <div className="mail-messages">
        {detail.messages.map((m) => (
          <MessageCard
            key={m.id}
            threadId={thread.id}
            message={m}
            expanded={expanded.has(m.id)}
            onToggle={() => toggle(m.id)}
            onCompose={onCompose ? (mode) => onCompose(mode, thread, m) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

/** Reading pane. Opening a thread is what marks it read — the server does that, not us. */
export function MailThreadView({
  threadId,
  onBack,
  onLoaded,
  onChanged,
  onCompose,
}: {
  threadId: string;
  onBack: () => void;
  onLoaded: (detail: MailThreadDetailDto) => void;
  onChanged: (change: ThreadChange) => void;
  onCompose?: ComposeRequest;
}) {
  const { data, loading, error, reload } = useApiResource(() => getThread(threadId), [threadId]);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    if (data && !loading && data.thread.id === threadId) onLoadedRef.current(data);
  }, [data, loading, threadId]);

  if (loading) return <div className="mail-list-state" role="status">Loading conversation…</div>;
  if (error || !data) {
    return (
      <div className="mail-list-state">
        <div role="alert" className="error-banner">
          {error ?? 'Could not load the conversation'}{' '}
          <button type="button" className="btn btn-xs" onClick={reload}>
            Retry
          </button>
        </div>
        <button type="button" className="btn btn-xs mail-back" onClick={onBack}>
          <ArrowLeft size={14} aria-hidden /> Back
        </button>
      </div>
    );
  }
  return <ThreadBody key={data.thread.id} detail={data} onBack={onBack} onChanged={onChanged} onCompose={onCompose} />;
}
